import styled from '@emotion/styled'
import cx from 'classnames'
import { useRef, type AriaRole, type ElementType, type PropsWithChildren } from 'react'

import { clearBuilderProps } from '@/legacy/core/utils/dom/clearBuilderProps'
import { sanitizeUrl } from '@/legacy/core/utils/dom/security'
import { useForceFocusOnElement } from '@/legacy/hooks/useForceFocusOnElement'
import { CONFIG } from '@/ui/shared/styles'

enum PADDING_SIZES {
  none = '0',
  xs = '0.25rem',
  md = '0.5rem',
  lg = '1rem',
  xl = '1.5rem',
}

const CardStyles = styled.a<{ padding?: PADDING_SIZES }>((props) => ({
  display: 'block',
  borderRadius: '0.5rem',
  padding: props.padding,
  boxShadow: 'var(--theme-box-shadow)',
  background: 'rgba(var(--theme-snow))',
  overflow: 'hidden',
  transition: 'var(--theme-transition) box-shadow',

  '&[href]:hover': {
    boxShadow: '0 0 0 1px rgba(var(--theme-accent)), var(--theme-box-shadow)',
  },

  '&[href]:active': {
    boxShadow: CONFIG.theme.shadows.primary,
  },
}))

type CardProps = PropsWithChildren<{
  padding?: keyof typeof PADDING_SIZES
  className?: string
  role?: AriaRole
  tag?: ElementType
  url?: string
  type?: string
  forceFocus?: boolean
  target?: '_blank' | '_self' | '_parent' | '_top'
}>

const Card = ({
  tag = 'div',
  padding = 'none',
  forceFocus = false,
  className,
  children,
  url,
  ...props
}: CardProps) => {
  clearBuilderProps(props)

  const cardRef = useRef(null)
  useForceFocusOnElement(cardRef, forceFocus)

  return (
    <CardStyles
      ref={cardRef}
      as={tag}
      padding={PADDING_SIZES[padding]}
      className={cx(className)}
      href={sanitizeUrl(url)}
      {...props}
    >
      {children}
    </CardStyles>
  )
}

export default Card
